/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class ObservationProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Load the EXIF data from an image file and attach it to an observation object
	 * @param {Object} pNewObservationObject - The observation object to attach the EXIF data to
	 * @param {Object} pFileDetails - The details of the file to upload
	 * @param {String} pDatumType - The type of the datum to upload
	 * @return {Promise<Object>} - A promise that resolves to the observation object with the EXIF data attached
	 */
	async attachEXIFDatumToObservation(pNewObservationObject, pFileDetails, pDatumType)
	{
		return new Promise((resolve, reject) =>
		{
			// fetch any EXIF data for the observation -- DOCS: https://github.com/exif-heic-js/exif-heic-js
			const readerExif = new FileReader();
			const methodName = pFileDetails.File.type.toLowerCase() === 'image/heic' ? 'findEXIFinHEIC' : 'findEXIFinJPEG';
			this.pict.log.info('[Image] Reading EXIF Data from file:', pFileDetails.File.type);
			this.pict.log.info('[Image] Using EXIF Method:', methodName);
			// on load of EXIF Data
			readerExif.onload = (buffer) =>
			{
				// take the exif data and set it on the File Object first (so we can use it later)
				const exifData = window[methodName](buffer.target.result);

				if (exifData && exifData.GPSLongitude && exifData.GPSLatitude)
				{
					let tmpLongitude = this.convertEXIFDataToGeolocation(exifData.GPSLongitude[0], exifData.GPSLongitude[1], exifData.GPSLongitude[2], exifData.GPSLongitudeRef);
					tmpLongitude = parseFloat(tmpLongitude.toFixed(8));
					let tmpLatitude = this.convertEXIFDataToGeolocation(exifData.GPSLatitude[0], exifData.GPSLatitude[1], exifData.GPSLatitude[2], exifData.GPSLatitudeRef);
					tmpLatitude = parseFloat(tmpLatitude.toFixed(8));
					this.pict.log.info('[Image] Found EXIF Data: lat/long', (tmpLatitude + ', ' + tmpLongitude));

					if (tmpLatitude && tmpLongitude)
					{
						// set the file details (used below in the generic datum fields loop for Observation Datum)
						pFileDetails.File.Latitude = tmpLatitude;
						pFileDetails.File.Longitude = tmpLongitude;
						// set the observation object's dedicated lat/long Capture fields
						pNewObservationObject.CaptureLatitude = tmpLatitude;
						pNewObservationObject.CaptureLongitude = tmpLongitude;
					}
				}
				// set altitude (store on file object)
				if (exifData && exifData.GPSAltitude && Object.keys(exifData.GPSAltitude)?.length)
				{
					pFileDetails.File.Altitude = parseFloat(exifData.GPSAltitude);
					this.pict.log.info('[Image] Found EXIF Data: altitude', exifData.GPSAltitude);
				}

				// set any additional EXIF data from the File object to the Observation Datum fields, if found
				const datumKeys = [ 'Altitude', 'DeviceTilt', 'HeadingMagnetic', 'HeadingTrue', 'HorizontalAccuracy', 'Latitude', 'Longitude', 'VerticalAccuracy' ];
				datumKeys.forEach((key) =>
				{
					if (pFileDetails.File[key])
					{
						pNewObservationObject.Details[pDatumType][0].Datum[key] = pFileDetails.File[key];
					}
				});
				resolve(pNewObservationObject);
			};
			// call the methods above by reading the file as an array buffer
			readerExif.readAsArrayBuffer(pFileDetails.File);
		});
	}

	/**
	 * Broadcast a document change to other applications listening for changes (FB Web, etc.)
	 * @param {Number} pIDDocument - The ID of the document that was changed
	 * @param {Number} pIDProject - The ID of the project that the document belongs to
	 * @return {void}
	 */
	broadcastDocumentChangeOnObservationJoin = (pIDDocument, pIDProject) =>
	{
		if (!pIDDocument && !pIDProject)
		{
			this.pict.log.warn('Cannot broadcast document change - missing required data (need either IDDocument and/or IDProject).');
			return;
		}
		if (window.localStorage)
		{
			// we are doing a set, then remove in this same browser tab, however, other browser tabs will see the set action
			// and can do a callback via the watcher they should have applied (listed below)

			// NOTE: Some of this can't be known unless we fetch the document record, so we mimic this as a courtesy to other apps for now...
			//	   The primary document/app's saving method is still the best way to refresh parent/external views watching changes.
			const tmpDocumentChange = JSON.stringify({
				action: 'ObservationsAdded',
				updateTime: new Date(),
				IDDocument: pIDDocument,
				IDProject: pIDProject,
			});
			window.localStorage.setItem('documentChange', tmpDocumentChange);
			window.localStorage.removeItem('documentChange');

			// a generic watcher with jQuery for usage

			// // the receiving method an app should use to 'catch' this broadcast
			// $(window).on('storage', function(ev) {
			//	 // if this isn't the storage we're looking for, we're done here
			//	 if (ev.originalEvent.key !== 'documentChange') {
			//		 return;
			//	 }
			//	 // parse the stringy data we should be getting
			//	 let obj = ev.originalEvent.newValue && ev.originalEvent.newValue.length ? JSON.parse(ev.originalEvent.newValue) : {};
			//	 // on save or on status change
			//	 if (obj.action === 'Save') {
			//		 console.log(`A form was saved in another tab, we should probably update/refresh data?`);
			//		 // set your app action callback here!
			//	 } else if (obj.action === 'ApprovalStatusChange') {
			//		 console.log(`A form had a status change in another tab, we should probably update/refresh data?`);
			//		 // set your app action callback here!
			//	 }
			// });
		}
	};

	/**
	 * Convert EXIF data to a geolocation
	 * @param {Number} degrees - The degrees of the geolocation
	 * @param {Number} minutes - The minutes of the geolocation
	 * @param {Number} seconds - The seconds of the geolocation
	 * @param {String} direction - The direction of the geolocation
	 * @return {Number} - The geolocation
	 * @example
	 * convertEXIFDataToGeolocation(40, 26, 46.8, 'N'); // returns 40.44633333333333
	 * convertEXIFDataToGeolocation(79, 58, 56.4, 'W'); // returns -79.98233333333333
	 */
	convertEXIFDataToGeolocation = (degrees, minutes, seconds, direction) =>
	{
		let dd = degrees + minutes/60 + seconds/(60*60);
		// Don't do anything for N or E
		if (direction == 'S' || direction == 'W')
		{
			dd = dd * -1;
		}
		return dd;
	};

	/**
	 * Get the observation by ID
	 * @param {Number} pIDObservation - The ID of the observation to get
	 * @param {Function} [pCallback] - The callback function to call when the observation is fetched
	 * @return {Promise}
	 * @async
	 */
	async getObservationByID(pIDObservation, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Observation/${pIDObservation}`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching observation: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching observation: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Extract file observation page info.
	 * @param {Number} pIDObservation - The ID of the document that was changed
	 * @param {Function} pCallback - Callback to return the extracted info.
	 */
	async extractInfoByID(pIDObservation, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Observation/${pIDObservation}/ExtractInfo`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error extracting info for observation: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error extracting info for observation: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Extract file observation page info.
	 * @param {Number} IDObservation - The ID of the File Observation.
	 * @param {Object} boundaries - The ID of the document that was changed.
	 * @param {Function} pCallback - Callback to return the subImage Observation.
	 */
	async extractSubImage(IDObservation, boundaries, pCallback)
	{
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		const tmpURL = `Observation/${ IDObservation }/Extract/Clone`;
		await new Promise((resolve, reject) => this.pict.fable.services.HeadlightRestClient.postJSON(tmpURL, boundaries,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error creating observation: `, pError);
					reject(new Error(`Error creating observation: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error creating observation: `, pBody.Error);
					reject(new Error(`Error creating observation: ` + pBody.Error));
				}
				else if (pBody)
				{
					resolve(pBody);
					pCallback(pBody);
				}
				this.pict.providers.UploadTracking.untrackUpload(uploadID);
			},
		));
	}

	async getObservationByIDList(pIDObservationList)
	{
		if (!Array.isArray(pIDObservationList) || pIDObservationList.length === 0)
		{
			return [];
		}
		// Concurrent callers asking for the same ids (several card decks marshaling at once) share one
		// request. Each gets its own record copies, since callers stamp per-namespace ordinals on them.
		const tmpRequestKey = pIDObservationList.map(String).sort().join(',');
		this._observationListRequests = this._observationListRequests || {};
		if (!this._observationListRequests[tmpRequestKey])
		{
			this._observationListRequests[tmpRequestKey] = new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Observations/FilteredTo/FBL~IDObservation~INN~${pIDObservationList.join(',')}`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching observations by id list: `, pError);
							reject(pError);
						}
						else
						{
							resolve(pBody);
						}
					});
			}).finally(() =>
			{
				delete this._observationListRequests[tmpRequestKey];
			});
		}
		const tmpRecords = await this._observationListRequests[tmpRequestKey];
		return Array.isArray(tmpRecords) ? tmpRecords.map((pRecord) => Object.assign({}, pRecord)) : tmpRecords;
	}

	/**
	 * Get the tag types for an observation
	 * @param {Object} pObservation - The observation to get the tag types for
	 * @param {Number} pIDObservation - The ID of the observation to get the tag types for
	 * @return {Array<Object>} - An array of tag types for the observation
	 */
	getObservationTagTypes(pObservation, pIDObservation)
	{
		const tmpObservation = pObservation;

		// Build a map of all tag types for this observation, ignoring the SAVE tag.
		const tmpTagTypes = [];
		const tmpObservationTagSet = Object.keys(tmpObservation.Labels);

		for (let i = 0; i < tmpObservationTagSet.length; i++)
		{
			if (tmpObservationTagSet[i] !== 'SAVE')
			{
				tmpTagTypes.push({ TagType: tmpObservationTagSet[i], TagCount: tmpObservation.Labels[tmpObservationTagSet[i]].length });
			}
		}

		return tmpTagTypes;
	}

	/**
	 * Get the tags for an observation of a specific type
	 * @param {Object} pObservation - The observation to get the tags for
	 * @param {String} pTagType - The type of the tags to get
	 * @return {Array<String>} - An array of tags for the observation of the specified type
	 */
	getObservationTags(pObservation, pTagType)
	{
		const tmpObservation = pObservation;

		// Build a map of the tags for this type.
		const tmpTags = [];

		if (tmpObservation.Labels[pTagType])
		{
			const tmpLabelSet = tmpObservation.Labels[pTagType];

			for (let i = 0; i < tmpLabelSet.length; i++)
			{
				tmpTags.push(tmpLabelSet[i]);
			}
		}

		return tmpTags;
	}

	/**
	 * Get the observation types for a project or customer
	 * @param {Number} [pIDProject] - The ID of the project to get the observation types for
	 * @param {Function} [pCallback] - The callback function to call when the observation types are fetched
	 * @async
	 */
	async getObservationTypeManifest(pIDProject, pCallback)
	{
		let tmpURL = ``;
		// TODO: Come back to this later for Project level manifest joins - we need to filter by customer for now
		// if (pIDProject)
		// {
		// 	tmpURL = `ProjectObservationManifestJoins/By/IDProject/${pIDProject}`;
		// }
		// else
		// {
		tmpURL = `ObservationManifests`;
		// }

		await new Promise((resolve, reject) => this.pict.fable.services.HeadlightRestClient.getJSON(tmpURL,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error fetching ObservationManifests: `, pError);
					reject(new Error(`Error fetching ObservationManifests: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error fetching ObservationManifests: `, pBody.Error);
					reject(new Error(`Error fetching ObservationManifests: ` + pBody.Error));
				}
				else if (pBody)
				{
					// filter out duplicates if we have them
					const tmpUniqueObservationTypes = [];
					const tmpUniqueObservationTypeMap = {};
					pBody.forEach((item) =>
					{
						// For PROJECTS, we need to filter by the project ID
						// if (!tmpUniqueObservationTypeMap[item.ObservationManifestName]) {
						// 	tmpUniqueObservationTypeMap[item.ObservationManifestName] = true;
						// 	tmpUniqueObservationTypes.push(item);
						// }
						// For CUSTOMERS (data format is different)
						if (!tmpUniqueObservationTypeMap[item.Name])
						{
							tmpUniqueObservationTypeMap[item.Name] = true;
							tmpUniqueObservationTypes.push(item);
						}
					});
					resolve(tmpUniqueObservationTypes);
					// if we have a callback, run it now
					if (pCallback)
					{
						pCallback(tmpUniqueObservationTypes);
					}
				}
			},
		));
	}

	/**
	 * Get the class for a label based on the observation tag object
	 * @param {Object} pTagObject - The tag object to get the class for
	 * @return {String} - The class for the label
	 * @example
	 * getLabelClassFromObservationTagObject({ TagType: 'Tag' }); // returns 'is-primary-25'
	 * getLabelClassFromObservationTagObject({ TagType: 'BidItem' }); // returns 'is-info-25'
	 */
	getLabelClassFromObservationTagObject(pTagObject)
	{
		let tmpClass = 'is-dark';

		if (!pTagObject || !pTagObject.TagType)
		{
			return tmpClass;
		}
		// TODO: revisit these colors for consistency and color blindness
		switch (pTagObject.TagType)
		{
		case 'Tag':
			tmpClass = 'has-text-white has-background-primary-25';
			break;
		case 'BidItem':
			tmpClass = 'has-text-white has-background-info-25';
			break;
		case 'Contractor':
			tmpClass = 'has-text-white has-background-link-25';
			break;
		case 'LineItem':
			tmpClass = 'has-text-white has-background-link-15';
			break;
		case 'Equipment':
			tmpClass = 'has-text-white has-background-info-15';
			break;
		}
		return tmpClass;
	}

	/**
	 * Get the display mapped details of an observation type
	 * @param {String} pObservationType - The type of the observation to get details for
	 * @return {Object} - An object with the display details of the observation type
	 * @example
	 * getObservationMappedDetails('Legacy Audio'); // returns { Icon: 'fa-volume-up', Type: 'Audio' }
	 * getObservationMappedDetails('Legacy Image'); // returns { Icon: 'fa-image', Type: 'Image' }
	 */
	getObservationMappedDetails = (pObservationType) =>
	{
		// TODO: this stuff should be moved to a configuration file or a centralized location for consistency
		const observationIconMap =
		{
			'File': 'fa-file',
			'Legacy Audio': 'fa-volume-up',
			'Legacy Equipment': 'fa-hammer',
			'Legacy Image': 'fa-image',
			'Legacy Narrative': 'fa-book',
			'Legacy Personnel': 'fa-user',
			'Legacy Start/Stop Work': 'fa-clock',
			'Legacy Temperature': 'fa-thermometer-half',
			'Legacy Traffic Control': 'fa-traffic-light-stop',
			'Legacy Video': 'fa-video',
			'Legacy Weather': 'fa-cloud-sun-rain',
			'WorkItem': 'fa-tasks',
		};
		const observationTypeMap =
		{
			'File': 'File',
			'Legacy Audio': 'Audio',
			'Legacy Density': 'Density Measurement',
			'Legacy Image': 'Image',
			'Legacy Narrative': 'Narrative',
			'Legacy Equipment': 'Equipment',
			'Legacy Materials': 'Material',
			'Legacy Personnel': 'Personnel',
			'Legacy Start/Stop Work': 'Start/Stop',
			'Legacy Temperature': 'Temperature',
			'Legacy Traffic Control': 'Traffic Control',
			'Legacy Video': 'Video',
			'Legacy Weather': 'Weather',
			'WorkItem': 'Work Item',
		};
		return {
			Icon: observationIconMap[pObservationType] || 'fa-table',
			Type: observationTypeMap[pObservationType] || pObservationType,
		};
	};

	/**
	 * Get the observation search count
	 * @param {Object} pSearchQuery - The search query object to use when performing the search
	 * @param {Object} pOptions - Contains the pagination options
	 * @param {Function} pCallback - The callback function to call when the search is complete
	 * @async
	 * @example
	 * getObservationSearchCount({ IDProject: 1234 }, (count) => { console.log(count); });
	 */
	async getObservationSearchCount(pSearchQuery, pOptions, pCallback)
	{
		const tmpURL = `ObservationsFilter/Count`;
		this.pict.HeadlightRestClient.postJSON(tmpURL, pSearchQuery,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error performing request for Count: ${pError}`, pError);
				}
				pCallback(pBody?.Count || 0);
			});
	}

	/**
	 * Get the observation search results
	 * @param {Object} pSearchQuery - The search query object to use when performing the search
	 * @param {Object} pOptions - Contains the pagination options
	 * @param {Function} pCallback - The callback function to call when the search is complete
	 * @async
	 * @example
	 * getObservationSearchResults({ IDProject: 1234 }, (results) => { console.log(results); });
	 */
	async getObservationSearchResults(pSearchQuery, pOptions, pCallback)
	{
		const tmpURL = `ObservationsFilter`;
		const currentPage = pOptions?.CurrentPage || 1;
		const limit = pOptions?.PageSize || 25;
		const startCount = (currentPage - 1) * limit;
		const endCount = currentPage * limit;
		this.pict.HeadlightRestClient.postJSON(`${tmpURL}/${startCount}/${endCount}`, pSearchQuery,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error performing search: ${pError}`, pError);
				}
				pCallback(Array.isArray(pBody) ? pBody : []);
			});
	}

	/**
	 * Group all passed observations by their Observation Type, returning an object with the observations grouped by type
	 * @param {Array} pObservations - The observations to group
	 * @return {Object} - An object with the observations grouped by their Observation Type
	 */
	groupObservationsByType = (pObservations) =>
	{
		const tmpGroupedObservations = {};
		if (!pObservations || !Array.isArray(pObservations))
		{
			return tmpGroupedObservations;
		}
		pObservations?.forEach((observation) =>
		{
			const tmpType = observation.ObservationType;
			if (!tmpGroupedObservations[tmpType])
			{
				tmpGroupedObservations[tmpType] =
				{
					ObservationList: [],
					ObservationType: observation.ObservationType,
					ObservationTypeDetails: this.getObservationMappedDetails(observation.ObservationType),
				};
			}
			tmpGroupedObservations[tmpType].ObservationList.push(observation);
		});
		return tmpGroupedObservations;
	};

	/**
	 * Cache busting for observation images of type (standard, preview and thumbnail-dense) for the observation.
	 * @param {Number} idObs - The ID of the observation to refresh.
	 * @param {Array} timeSet - An array of times to trigger a refresh on.
	 */
	cacheBustObsImages(idObs, timeSet)
	{
		const images = document.querySelectorAll(`[src*="/1.0/Observation/${ idObs }/Image/"]`);
		const outLineClassThick = 'has-animated-thick-outline';
		const defaultOutlineClass = 'has-animated-outline';
		let outLineClass = defaultOutlineClass;
		if (images.length)
		{
			images.forEach((i) =>
			{
				outLineClass = defaultOutlineClass;
				if (i instanceof HTMLImageElement && i.src?.includes('Preview'))
				{
					outLineClass = outLineClassThick;
				}
				i.classList.add(outLineClass);
			});
		}
		for (const time of timeSet)
		{
			setTimeout(()=>
			{
				const images = document.querySelectorAll(`[src*="/1.0/Observation/${ idObs }/Image/"]`);
				if (images.length)
				{
					images.forEach((i) =>
					{
						if (!(i instanceof HTMLImageElement))
						{
							return;
						}
						const srcWStamp = i.src?.split('?t')?.[0] + '?t=' + new Date().getTime();
						i.src = srcWStamp;
						if (time === timeSet[timeSet.length - 1])
						{
							outLineClass = defaultOutlineClass;
							if (i.src?.includes('Preview'))
							{
								outLineClass = outLineClassThick;
							}
							i.classList.remove(outLineClass);
						}
					});
				}
			}, time);
		}
	}

	/**
	 * Do a direct observation join to a document record (vs saving the document with the observation attached)
	 * @param {Number} pIDObservation - The ID of the observation to join
	 * @param {Number} pIDDocument - The ID of the document to join the observation to
	 * @param {Function} pCallback - The callback function to call when the observation is joined
	 * @example
	 * joinObservationToDocumentRecord(123, (response) => { console.log(response); });
	 */
	async joinObservationToDocumentRecord(pIDObservation, pIDDocument, pCallback)
	{
		// If we passed a document ID, join the observations to the document
		// NOTE: if you don't want to join the observations to the document, just don't pass an IDDocument
		if (!pIDDocument)
		{
			this.pict.log.warn('No document ID provided - skipping observation join.');
			return;
		}

		const tmpURL = `Document/${pIDDocument}/AddObservation/${pIDObservation}`;
		await new Promise((resolve, reject) => this.pict.fable.services.HeadlightRestClient.getJSON(tmpURL,
			async (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error updating document observation join: `, pError);
					reject(new Error(`Error updating document observation join: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error updating document observation join: `, pBody.Error);
					reject(new Error(`Error updating document observation join: ` + pBody.Error));
				}
				else if (pBody)
				{
					if (this.pict.AppData?.DocumentData?.ReportData?.IDDocument === pIDDocument)
					{
						const obs = await this.getObservationByID(pIDObservation);
						this.pict.AppData.DocumentData.ReportData.Observations.push(obs);
						this.pict.providers.MediaRegistry.refreshDocumentObservations();
					}
					resolve(pBody);
					// if we have a callback, run it now
					if (pCallback)
					{
						pCallback(pBody);
					}
					// broadcast the document change for FB and other apps to pick up
					this.broadcastDocumentChangeOnObservationJoin(pIDObservation, pIDDocument);
				}
			},
		));
	}

	/**
	 * Do a direct observation unjoin from a document record (vs saving the document with the observation detached)
	 * @param {Number} pIDObservation - The ID of the observation to unjoin
	 * @param {Number} pIDDocument - The ID of the document to unjoin the observation from
	 * @param {Function} pCallback - The callback function to call when the observation is unjoined
	 * @example
	 * unjoinObservationFromDocumentRecord(123, (response) => { console.log(response); });
	 */
	unjoinObservationFromDocumentRecord(pIDObservation, pIDDocument, pCallback)
	{
		// If we passed a document ID, join the observations to the document
		// NOTE: if you don't want to join the observations to the document, just don't pass an IDDocument
		if (!pIDDocument)
		{
			this.pict.log.warn('No document ID provided - skipping observation join.');
			return;
		}

		const tmpURL = `Document/${pIDDocument}/RemoveObservation/${pIDObservation}`;
		this.pict.fable.services.HeadlightRestClient.getJSON(tmpURL,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error updating document observation join: `, pError);
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error updating document observation join: `, pBody.Error);
				}
				else if (pBody)
				{
					if (this.pict.AppData?.DocumentData?.ReportData?.IDDocument === pIDDocument)
					{
						this.pict.AppData.DocumentData.ReportData.Observations = this.pict.AppData.DocumentData.ReportData.Observations.filter((o) => o.IDObservation !== pIDObservation);
						this.pict.providers.MediaRegistry.refreshDocumentObservations();
					}
					// if we have a callback, run it now
					if (pCallback)
					{
						pCallback(pBody);
					}
					// broadcast the document change for FB and other apps to pick up
					this.broadcastDocumentChangeOnObservationJoin(pIDObservation, pIDDocument);
				}
			},
		);
	}

	/**
	 * This method performs an observation search based on the current search filters.
	 * @param {Object} pOptions - The options to use when performing the search.
	 * @param {Function} pLocalCallback - The callback function to call when the search is complete
	 * @async
	 */
	async performObservationSearch(pOptions = {}, pLocalCallback)
	{
		// TODO: when adding sorting to this, add {~T:ObservationSearch-Entry-SortBy~} back to ObservationSearch-Entry template
		const tmpSearchQuery = {};
		// handle the search filters
		if (pOptions?.SearchText && pOptions.SearchText?.trim().length > 0)
		{
			tmpSearchQuery['SearchText'] = pOptions.SearchText;
		}
		if (pOptions?.Projects && pOptions.Projects.length > 0)
		{
			tmpSearchQuery['IDProject'] = pOptions.Projects;
		}
		if (pOptions?.ObservationTypes && pOptions.ObservationTypes.length > 0)
		{
			tmpSearchQuery['ObservationType'] = pOptions.ObservationTypes;
		}
		if (pOptions?.StartDate && pOptions.StartDate.length > 0)
		{
			tmpSearchQuery['DesignatedTime'] = {
				From: pOptions.StartDate,
			};
		}
		if (pOptions?.EndDate && pOptions.EndDate.length > 0)
		{
			if (!tmpSearchQuery['DesignatedTime'])
			{
				tmpSearchQuery['DesignatedTime'] = {};
			}
			tmpSearchQuery['DesignatedTime'] = {
				To: pOptions.EndDate,
			};
		}

		try
		{
			// get the count of results first
			this.getObservationSearchCount(tmpSearchQuery, pOptions, (pCountResults) =>
			{
				// get the results
				this.getObservationSearchResults(tmpSearchQuery, pOptions, (pSearchResults) =>
				{
					// if there is a local callback function, call it
					if (pLocalCallback)
					{
						// call the local callback with the results of both the count and the search
						pLocalCallback(pSearchResults, pCountResults);
					}
				});
			});
		}
		catch (error)
		{
			this.pict.log.error('Error performing observation search:', error);
			this.pict.views.HeadlightPanels.toast({
				message: `<strong>Error performing observation search:</strong><br/><em>${error.message || error}</em>`,
				type: 'is-danger',
			});
			// if there is a local callback function, call it
			if (pLocalCallback)
			{
				pLocalCallback([]);
			}
		}
	}

	/**
	 * Format the observation record to be uploaded to the server. This also attaches the EXIF data to the observation if it is available.
	 * @param {Object} pFileDetails - The details of the file to upload
	 * @param {Number} pIDProject - The ID of the project to upload the observation to
	 * @param {Function} onObservationResult - The callback function to call when the observation is posted
	 * @param {Function} onProgressChange - The callback function to call when the progress of the upload changes
	 */
	async prepareObservationObjectFromFile(pFileDetails, pIDProject, onObservationResult, onProgressChange)
	{
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		const datumType = pFileDetails?.ObservationType.replace('Legacy ', ''); // probably a better way to get these (manifest?)
		let tmpObservation = {
			Name: pFileDetails.Name || pFileDetails.FileName || `New ${ pFileDetails.ObservationType } Observation`,
			Description: pFileDetails.Description,
			Tags: Array.isArray(pFileDetails.Tags) ? pFileDetails.Tags : [ pFileDetails.Tags ],
			ObservationType: pFileDetails.ObservationType,
			Details: {},
			IDObservation: 0,
			IDProject: pIDProject,
			// set by Exif data below, if available
			CaptureLatitude: '0.00000000',
			CaptureLongitude: '0.00000000',
			// TODO: add these to the UI and allow the user to set them
			UserLatitude: pFileDetails.UserLatitude || '0.00000000',
			UserLongitude: pFileDetails.UserLongitude || '0.00000000',
			AlignmentStation: pFileDetails.AlignmentStation || 0,
			AlignmentOffset: pFileDetails.AlignmentOffset || 0,
		};
		// format this for the API
		tmpObservation.Details[datumType] = [ {
			'ArtifactType': datumType,
			'IDArtifact': 0,
			'Datum': {
				'FileName': pFileDetails.FileName,
				'FileMimeType': pFileDetails.File.type,
			},
		} ];
		// additional EXIF Data fields that might be set on images needs to be processed first
		if (pFileDetails.File.type.toLowerCase() === 'image/heic' || pFileDetails.File.type.toLowerCase() === 'image/jpg' || pFileDetails.File.type.toLowerCase() === 'image/jpeg')
		{
			await this.attachEXIFDatumToObservation(tmpObservation, pFileDetails, datumType);
		}

		const geoCoords = await this.pict.providers.GeoProvider.getGeoCoords();
		tmpObservation = Object.assign(tmpObservation, geoCoords);

		try
		{
			await this.uploadObservation(tmpObservation, pFileDetails, datumType, onObservationResult, onProgressChange);
		}
		catch (e)
		{
			this.pict.log.error('Error uploading observation: ', e);
			this.pict.views.HeadlightPanels.toast({
				message: `<strong>Error uploading observation:</strong><br/><em>${e.message || e}</em>`,
				type: 'is-danger',
			});
		}
		this.pict.providers.UploadTracking.untrackUpload(uploadID);
	}

	/**
	 * This method is used to return the appropriate icon for the passed file type.
	 * @param {String} pFileType
	 * @return {String}
	 */
	returnFileIconFromFileType = (pFileType) =>
	{
		switch (pFileType)
		{
		case 'image/jpeg':
		case 'image/png':
		case 'image/gif':
		case 'image/bmp':
			return 'fa-file-image';
		case 'video/mp4':
		case 'video/mov':
		case 'video/avi':
		case 'video/wmv':
			return 'fa-file-video';
		case 'audio/mp3':
		case 'audio/wav':
		case 'audio/aac':
		case 'audio/wma':
		case 'audio/mpeg':
			return 'fa-file-audio';
		case 'application/pdf':
			return 'fa-file-pdf';
		case 'application/msword':
		case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
			return 'fa-file-word';
		case 'application/vnd.ms-excel':
		case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
			return 'fa-file-excel';
		case 'application/vnd.ms-powerpoint':
		case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
			return 'fa-file-powerpoint';
		case 'text/plain':
			return 'fa-file-lines';
		case 'text/csv':
			return 'fa-file-csv';
		case 'application/zip':
		case 'application/x-7z-compressed':
		case 'application/x-rar-compressed':
		case 'application/x-tar':
		case 'application/gzip':
			return 'fa-file-zipper';
		case 'text/html':
			return 'fa-file-code';
		case 'application/json':
			return 'fa-file-code';
		case 'application/xml':
			return 'fa-file-code';
		default:
			return 'fa-file';
		}
	};

	/**
	 * This method is used to return the proper Observation Type for the passed file type.
	 * @param {String} pFileType
	 * @return {String}
	 */
	returnObservationTypeFromFileType = (pFileType) =>
	{
		switch (pFileType)
		{
		case 'image/jpeg':
		case 'image/png':
		case 'image/gif':
		case 'image/bmp':
			return 'Legacy Image';
		case 'video/mp4':
		case 'video/mov':
		case 'video/avi':
		case 'video/wmv':
			return 'Legacy Video';
		case 'audio/mp3':
		case 'audio/wav':
		case 'audio/aac':
		case 'audio/wma':
		case 'audio/mpeg':
			return 'Legacy Audio';
		default:
			return 'File';
		}
	};

	/**
	 * Get the file type from an observation record
	 * @param {Object} pObservation - The observation record
	 * @return {String} - The file type of the observation
	 */
	getObservationFileType(pObservation)
	{
		if (!pObservation || typeof pObservation !== 'object')
		{
			this.pict.log.error('Invalid observation record');
			return '';
		}
		// return the file type based on the observation type
		switch (pObservation.ObservationType)
		{
		case 'File':
			return pObservation?.Details?.File?.[0]?.Datum?.FileMimeType;
		case 'Legacy Image':
			return pObservation?.Details?.Image?.[0]?.Datum?.FileMimeType;
		case 'Legacy Audio':
			return pObservation?.Details?.Audio?.[0]?.Datum?.FileMimeType;
		case 'Legacy Video':
			return pObservation?.Details?.Video?.[0]?.Datum?.FileMimeType;
		default:
			return '';
		}
	}

	/**
	 * Post a new Observation to the server and upload the associated file
	 * @param {Object} pObservationDetails - The details of the observation to post
	 * @param {Object} pFileDetails - The details of the file to upload
	 * @param {String} pDatumType - The type of the datum to upload
	 * @param {Function} onObservationResult - The callback function to call when the observation is posted
	 * @param {Function} onProgressChange - The callback function to call when the progress of the upload changes
	 */
	async uploadObservation(pObservationDetails, pFileDetails, pDatumType, onObservationResult, onProgressChange)
	{
		const tmpURL = `Observation/`;
		const newObservationRecord = await new Promise((resolve, reject) => this.pict.fable.services.HeadlightRestClient.postJSON(tmpURL, pObservationDetails,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error creating observation: `, pError);
					this.pict.views.HeadlightPanels.toast({
						message: `<strong>Error creating observation:</strong><br/><em>${pError.message || pError}</em>`,
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error creating observation: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error creating observation: `, pBody.Error);
					this.pict.views.HeadlightPanels.toast({
						message: `<strong>Error creating observation:</strong><br/><em>${pBody.Error.message || pBody.Error}</em>`,
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error creating observation: ` + pBody.Error));
				}
				else if (pBody)
				{
					resolve(pBody);
				}
			},
		));
		const artifact = newObservationRecord.Details[pDatumType][0];
		const file = pFileDetails.File;
		// show the progress bar update as the file is sent
		await this.uploadWithProgress(`Artifact/Media/${artifact.IDArtifact}/${artifact.Version}`, file, onProgressChange);
		// notify the caller AFTER the binary upload completes, not just after
		// the observation record is created — otherwise the upload panel closes
		// before large files finish transferring (race condition for >100MB files)
		onObservationResult(newObservationRecord);
	}

	/**
	 * Post an update to an Observation to the server.
	 * @param {Object} pObservationDetails - The details of the observation to post
	 * @param {Function} onObservationResult - The callback function to call when the observation is posted
	 */
	async updateObservation(pObservationDetails, onObservationResult)
	{
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		const tmpURL = `Observation/`;
		await new Promise((resolve, reject) => this.pict.fable.services.HeadlightRestClient.putJSON(tmpURL, pObservationDetails,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error updating observation: `, pError);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred updating the observation.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error updating observation: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error updating observation: `, pBody.Error);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred updating the observation.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error updating observation: ` + pBody.Error));
				}
				else if (pBody)
				{
					resolve(pBody);
					onObservationResult(pBody);
				}
				this.pict.providers.UploadTracking.untrackUpload(uploadID);
			},
		));
	}

	/**
	 * Upload a file to the server with progress updates.
	 *
	 * Uses HeadlightRestClient.postBinary() so the request participates
	 * in REST interception (offline mode routes to BlobStore).
	 *
	 * @param {string} pURL - The relative URL to upload the file to (e.g. /1.0/Artifact/Media/5/1)
	 * @param {File} pFile - The file to upload
	 * @param {function} fOnProgressChange - Callback with progress ratio (0.0 to 1.0)
	 * @returns {Promise<boolean>} Resolves to true if the upload was successful
	 */
	async uploadWithProgress(pURL, pFile, fOnProgressChange)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.fable.services.HeadlightRestClient.postBinary(
				pURL,
				pFile,
				pFile.type,
				(pError, pBody) =>
				{
					if (pError)
					{
						reject(pError);
					}
					else
					{
						resolve(true);
					}
				},
				fOnProgressChange
			);
		});
	}

	/**
	 * Download a binary file from an Artifact.
	 *
	 * Uses HeadlightRestClient.getBinaryBlob() so the request participates
	 * in REST interception (offline mode retrieves from BlobStore).
	 *
	 * @param {number|string} pArtifactID - The Artifact ID
	 * @param {number|string} pVersion - The Artifact version
	 * @returns {Promise<Blob>} The downloaded file as a Blob
	 */
	async downloadFileFromObservation(pArtifactID, pVersion)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.fable.services.HeadlightRestClient.getBinaryBlob(
				`Artifact/Media/${pArtifactID}/${pVersion}`,
				(pError, pBlob) =>
				{
					if (pError)
					{
						reject(pError);
					}
					else
					{
						resolve(pBlob);
					}
				}
			);
		});
	}

	/**
	 * Get a displayable URL for an observation's media.
	 *
	 * Returns a blob: URL from BlobStore if available offline,
	 * otherwise returns the server URL for online display.
	 *
	 * @param {object} pObservation - The observation record
	 * @param {string} pDatumType - The datum type key (e.g. 'Image', 'Video')
	 * @param {function} fCallback - Callback(pError, pURL)
	 */
	getObservationImageURL(pObservation, pDatumType, fCallback)
	{
		if (!pObservation || !pObservation.Details)
		{
			return fCallback(new Error('No observation or details provided'));
		}

		let tmpDetails = pObservation.Details;
		if (typeof tmpDetails === 'string')
		{
			try
			{
				tmpDetails = JSON.parse(tmpDetails);
			}
			catch (e)
			{
				return fCallback(new Error('Could not parse observation Details'));
			}
		}

		let tmpDatumEntries = tmpDetails[pDatumType];
		if (!Array.isArray(tmpDatumEntries) || tmpDatumEntries.length === 0)
		{
			return fCallback(new Error(`No ${pDatumType} datum entries found`));
		}

		let tmpEntry = tmpDatumEntries[0];
		let tmpIDArtifact = tmpEntry.IDArtifact;
		let tmpVersion = tmpEntry.Version || 1;

		if (!tmpIDArtifact)
		{
			return fCallback(new Error('No IDArtifact on datum entry'));
		}

		let tmpURL = `Artifact/Media/${tmpIDArtifact}/${tmpVersion}`;

		// Try to get the blob through HeadlightRestClient (interceptor will
		// return from BlobStore if offline, or network if online)
		this.pict.fable.services.HeadlightRestClient.getBinaryBlob(tmpURL,
			(pError, pBlob) =>
			{
				if (pError || !pBlob)
				{
					// Fall back to the server URL for direct browser loading
					let tmpServerURL = this.pict.fable.services.HeadlightRestClient.formatUrl(tmpURL, true);
					return fCallback(null, tmpServerURL);
				}

				// Create an Object URL from the blob for display
				try
				{
					let tmpBlobURL = URL.createObjectURL(pBlob);
					return fCallback(null, tmpBlobURL);
				}
				catch (e)
				{
					let tmpServerURL = this.pict.fable.services.HeadlightRestClient.formatUrl(tmpURL, true);
					return fCallback(null, tmpServerURL);
				}
			});
	}

	getLabelTypeFromObservation(pObservation)
	{
		if (pObservation && pObservation.ObservationType)
		{
			const tmpLabelType = this.getObservationMappedDetails(pObservation.ObservationType);
			return tmpLabelType;
		}
		return null;
	}
	/**
	 * Sorts the observations by a given key and order
	 * @param {Array} pObservations - The observations to sort
	 * @param {String} pSortBy - The key to sort by
	 * @param {String} pSortOrder - The order to sort by (asc or desc)
	 * @return {Array} - The sorted observations
	 */
	sortObservationsByKey(pObservations, pSortBy, pSortOrder)
	{
		if (!pObservations || !pObservations.length)
		{
			return pObservations;
		}
		if (!pSortBy)
		{
			this.pict.log.warn('No sort key provided - returning unsorted observations.');
			return pObservations;
		}
		// TODO: Add support for Labels and Datums
		const tmpSortOrder = pSortOrder && pSortOrder.toLowerCase() === 'desc' ? -1 : 1;
		const tmpSortedObservations = pObservations.sort((a, b) =>
		{
			const aValue = a[pSortBy];
			const bValue = b[pSortBy];
			if (aValue < bValue)
			{
				return -1 * tmpSortOrder;
			}
			if (aValue > bValue)
			{
				return 1 * tmpSortOrder;
			}
			return 0;
		});
		return tmpSortedObservations;
	}

	/**
	 * Handles filtering a list of observations based on the filter options passed in.
	 * This is used to apply the filters set in the options to the observation list after we get the search results back from the server.
	 * This is used in cases where a subset list of observations is already provided locally (ex: in the document data) and we want to apply
	 * the filters from options to that list without having to make another call to the server.
	 *
	 * Expected JSON PictForm structure:
	 * "FiltersObject": {
	 *     "Include": {
	 * 			"ObservationTypes": ["Type1", "Type2"],
	 * 			"Descriptions": ["Description1", "Description2"],
	 * 			"LabelTypes": [
	 * 				{
	 * 					"LabelType": "LabelType1",
	 * 					"Values": ["Value1", "Value2"]
	 * 				},
	 * 				{
	 * 					"LabelType": "LabelType2",
	 * 					"Values": ["Value3", "Value4"]
	 * 				}
	 * 			]
	 * 		}
	 * 		"Exclude": {
	 * 			"ObservationTypes": ["Type3", "Type4"],
	 * 			"Descriptions": ["Description3", "Description4"],
	 * 			"LabelTypes": [
	 * 				{
	 * 					"LabelType": "LabelType3",
	 * 					"Values": ["Value5", "Value6"]
	 * 				},
	 * 				{
	 * 					"LabelType": "LabelType4",
	 * 					"Values": ["Value7", "Value8"]
	 * 				}
	 * 			]
	 * 		}
	 * }
	 *
	 * @param {Object} pFilters - The filter options to apply to the observation list.
	 * @param {Array} pObservationList - The list of observations to be filtered.
	 * @return {Array} - The filtered list of observations after applying the filters from options.
	 */
	handleFilteringObservationsInList(pFilters, pObservationList)
	{
		// if there are no observations in the list, we can just return the empty list without doing any filtering
		if (!pObservationList || !pObservationList.length)
		{
			return pObservationList;
		}
		if (!pFilters || !Object.keys(pFilters).length)
		{
			this.pict.log.info(`No observation filters set in options. Returning unfiltered observation list.`);
			return pObservationList;
		}
		// apply the filters from options to the observation list. We apply the "Include" filters first, and then the "Exclude" filters second,
		// so that we can first narrow down the list based on what we want to include, and then remove any observations that we want to exclude from that narrowed down list.
		const { Include, Exclude } = pFilters;
		let filteredObservationList = pObservationList;
		// apply the "Include" filters first to narrow down the list
		if (Include && Object.keys(Include).length)
		{
			if (Include.ObservationTypes && Include.ObservationTypes.length)
			{
				filteredObservationList = filteredObservationList.filter((o) => Include.ObservationTypes.includes(o.ObservationType));
			}
			// complex LabelType structure needs to be traversed for observation labels
			if (Include.LabelTypes && Include.LabelTypes.length)
			{
				for (const labelFilter of Include.LabelTypes)
				{
					filteredObservationList = filteredObservationList.filter((o) =>
					{
						if (o.Labels.hasOwnProperty(labelFilter.Type) && o.Labels[labelFilter.Type].length)
						{
							return o.Labels[labelFilter.Type].find((l) => l.TagType === labelFilter.Type && labelFilter.Values.includes(l.Tag));
						}
						return false;
					});
				}
			}
		}
		// then apply the "Exclude" filters to remove any observations we don't want in the list after we've narrowed it down with the "Include" filters
		if (Exclude && Object.keys(Exclude).length)
		{
			if (Exclude.ObservationTypes && Exclude.ObservationTypes.length)
			{
				filteredObservationList = filteredObservationList.filter((o) => !Exclude.ObservationTypes.includes(o.ObservationType));
			}
			// complex LabelType structure needs to be traversed for observation labels
			if (Exclude.LabelTypes && Exclude.LabelTypes.length)
			{
				for (const labelFilter of Exclude.LabelTypes)
				{
					filteredObservationList = filteredObservationList.filter((o) =>
					{
						if (o.Labels.hasOwnProperty(labelFilter.Type) && o.Labels[labelFilter.Type].length)
						{
							return !o.Labels[labelFilter.Type].find((l) => l.TagType === labelFilter.Type && labelFilter.Values.includes(l.Tag));
						}
						return true;
					});
				}
			}
		}
		this.pict.log.info(`Applied observation filters. Original list had ${pObservationList.length} observations, after filtering it has ${filteredObservationList.length} observations.`);
		return filteredObservationList;
	}
}

module.exports = ObservationProvider;
module.exports.default_configuration = {};
